/**
 * useChatBot Hook
 *
 * Manages Intercom chat integration with channel-specific configuration and deep linking.
 * Provides unified interface for customer support across all tenant channels.
 *
 * Key architectural decisions:
 * - Channel-driven activation: Each tenant decides if chat support is enabled
 * - Lazy loading: Intercom SDK only boots when channel has chatbot_id configured
 * - Deep link support: Marketing campaigns can auto-launch chat via URL parameters
 *
 * Business requirements:
 * - Customer support costs scale with usage, so chat is opt-in per channel
 * - Marketing teams need ability to link directly to support chat from emails/ads
 * - User context (name, email) must be passed for personalized support experience
 * - Channel branding (colors) must be applied to maintain consistent UX
 *
 * Security considerations:
 * - Base64 encoded query params prevent casual URL manipulation of chat launch
 * - User details only shared after authentication to prevent data leakage
 * - Channel validation ensures chat only activates for paying tenants
 *
 * @see src/utils/constants.js - Intercom app configuration
 * @see docs/38_THIRD_PARTY_INTEGRATIONS.md - Intercom integration details
 */

import { useEffect, useState } from 'react';
import { useIntercom } from 'react-use-intercom';
import { useUserDetails } from '../features/authentication/useUserDetails';
import { useChannel } from './useChannel';
import { useSearchParams } from 'react-router-dom';

/**
 * Extended window interface for Intercom global API
 */
declare global {
  interface Window {
    Intercom?: (command: string, ...args: unknown[]) => void;
  }
}

/**
 * ChatBot result interface with all control functions
 */
export interface UseChatBotResult {
  /** Whether Intercom is enabled for this channel */
  enabled: boolean;
  /** Initialize Intercom with channel configuration */
  bootChatBot: () => void;
  /** Completely shutdown Intercom (cleanup) */
  shutdownChatBot: () => void;
  /** Update Intercom with custom data */
  updateChatBot: (data: { data: Record<string, unknown> }) => void;
  /** Hide the Intercom messenger panel */
  hideChatBot: () => void;
  /** Show/open the Intercom messenger panel */
  showChatBot: () => void;
  /** Hide the floating launcher button (does NOT open messenger) */
  hideLauncher: () => void;
  /** Restore the floating launcher button (does NOT open messenger) */
  showLauncher: () => void;
}

/**
 * Intercom chatbot integration hook with react-use-intercom library
 *
 * RESTORED: This is the original implementation using react-use-intercom
 * which provides robust lifecycle management and error handling.
 *
 * Key features restored:
 * - Deep linking support via ?q=base64 URL parameters
 * - Channel theming (action_color, background_color)
 * - Proper user name concatenation (given_name + family_name)
 * - hardShutdown functionality
 * - All original control functions (boot, shutdown, hide, show, update)
 *
 * @example
 * // Basic usage
 * const { enabled, showChatBot } = useChatBot();
 * if (enabled) {
 *   showChatBot(); // Open chat widget
 * }
 *
 * @example
 * // Deep link from marketing email
 * // URL: https://site.com?q=Y2hhdGJvdD1sYXVuY2g=
 * // Decodes to: chatbot=launch
 * // Auto-opens chat on page load
 */
export function useChatBot(): UseChatBotResult {
  const [enabled, setEnabled] = useState<boolean>(false);
  const [chatbotId, setChatBotId] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [launchChatBot, setLaunchChatBot] = useState<boolean>(false);

  const { boot, shutdown, hide, show, update, hardShutdown } = useIntercom();

  const { user, isLoading: isLoadingUser } = useUserDetails();

  const { channel, isLoading: isLoadingChannel } = useChannel();

  // WHY boot on channel load instead of component mount:
  // Prevents Intercom initialization for channels without support contracts
  // Reduces third-party script load for users who don't need support
  useEffect(() => {
    if (enabled && chatbotId && channel && !isLoadingChannel) {
      bootChatBot();
    }
  }, [enabled, chatbotId, channel]);

  // WHY deep link parsing via base64 encoded 'q' parameter:
  // Marketing emails/ads need to auto-launch support chat for specific campaigns
  // Base64 encoding prevents users from guessing parameter structure
  // Example: ?q=Y2hhdGJvdD1sYXVuY2g= decodes to "chatbot=launch"
  useEffect(() => {
    function getParams(): void {
      // EDGE CASE: Prevent multiple launch attempts if already triggered
      // Without this, browser back/forward navigation could re-trigger chat
      if (launchChatBot) return;
      const paramString = searchParams.get('q');

      if (!paramString || !chatbotId) return;

      try {
        // WHY atob for base64 decode: Standard encoding used by marketing tools
        // Provides obfuscation without encryption overhead
        const params = atob(paramString).split('&');
        const paramObj: Record<string, string> = {};

        for (let i = 0; i < params.length; i++) {
          const pair = params[i].split('=');
          // WHY replaceAll("%2F", "/"): URL encoding preservation through base64
          // Allows passing URL paths as parameter values for context
          paramObj[pair[0]] = pair[1]?.replaceAll('%2F', '/') || '';
        }

        if (chatbotId && paramObj['chatbot'] === 'launch') {
          setLaunchChatBot(true);
          // WHY delete 'q' after processing: Clean URL for better UX
          // Prevents confusion if user shares URL or bookmarks page
          searchParams.delete('q');
          setSearchParams(searchParams);
        }
      } catch (error) {
        console.error('Failed to parse chatbot deep link:', error);
      }
    }

    getParams();
  }, [searchParams, chatbotId]);

  // WHY channel-driven enablement:
  // Not all channels have support contracts - chat costs money per interaction
  // Channel.meta.chatbot_id presence indicates active Intercom subscription
  useEffect(() => {
    if (channel && channel?.meta?.chatbot_id) {
      setEnabled(true);
      setChatBotId(channel.meta.chatbot_id);
    }
  }, [channel]);

  // WHY separate effect for auto-launch:
  // Race condition prevention - chatbotId must be set before launch attempt
  // Deep link could arrive before channel config loads
  useEffect(() => {
    if (chatbotId && launchChatBot) {
      showChatBot();
    }
  }, [chatbotId, launchChatBot]);

  // WHY user details update in separate effect:
  // Privacy requirement - only share PII after user authentication
  // Allows anonymous browsing until user chooses to engage support
  useEffect(() => {
    if (user) addUserDetails();
  }, [user]);

  /**
   * Initialize Intercom with channel configuration
   *
   * RESTORED: Channel theming with action_color and background_color
   * RESTORED: Channel name passed to support agents
   */
  function bootChatBot(): void {
    // SAFEGUARD: Prevent accidental Intercom charges on disabled channels
    if (!enabled || !channel) return;

    const { theme_color, name, channel_id } = channel;

    // WHY window.Intercom instead of SDK method:
    // Intercom's React SDK has race conditions with dynamic app_id
    // Direct API calls ensure proper initialization sequence
    if (window.Intercom && chatbotId) {
      window.Intercom('boot', {
        app_id: chatbotId,
        // RESTORED: WHY duplicate color properties:
        // action_color: CTA buttons and links
        // background_color: Header and launcher button
        // Ensures complete brand consistency in chat widget
        action_color: theme_color,
        background_color: theme_color,
        // WHY capital 'C' in Channel:
        // Intercom custom attribute convention for non-standard fields
        // Allows support agents to see which club/channel user belongs to
        Channel: name,
        // DEV-1955 / DEV-1837: Pass channel_id so support agents can identify
        // the tenant without manual lookup. Custom attribute for Intercom dashboard.
        channel_id: channel_id,
      });
    }
  }

  /**
   * Completely shutdown Intercom
   */
  function shutdownChatBot(): void {
    if (!enabled) return;
    if (window.Intercom) {
      window.Intercom('shutdown');
    }
  }

  /**
   * Hide Intercom messenger
   */
  function hideChatBot(): void {
    if (!enabled) return;
    if (window.Intercom) {
      window.Intercom('hide');
    }
  }

  /**
   * Show Intercom messenger
   */
  function showChatBot(): void {
    if (!enabled) return;
    if (window.Intercom) {
      window.Intercom('show');
    }
  }

  /**
   * Add user details to Intercom
   *
   * RESTORED: Proper name concatenation (given_name + family_name)
   * instead of generic 'name' field
   */
  function addUserDetails(): void {
    if (!user || !window.Intercom) return;

    // WHY concatenate names instead of using display_name:
    // Intercom expects standard 'name' field for agent dashboard
    // Cognito stores names separately, must combine for proper display
    window.Intercom('update', {
      name: user?.given_name + ' ' + user?.family_name,
      email: user?.email,
    });
  }

  /**
   * Update Intercom with custom data
   *
   * @param payload - Object containing data to update
   */
  function updateChatBot(payload: { data: Record<string, unknown> }): void {
    if (!enabled || !window.Intercom) return;

    // WHY spread operator for data:
    // Allows flexible attribute updates without maintaining schema
    // Marketing team can add campaign tracking without code changes
    window.Intercom('update', { ...payload.data });
  }

  /**
   * Hide the default launcher button without opening the messenger.
   *
   * DEV-1955: Use this instead of hideChatBot when you want to suppress the
   * floating bubble (e.g. during auth flows) without triggering a messenger
   * open on cleanup. Pairs with showLauncher().
   */
  function hideLauncher(): void {
    if (!enabled || !window.Intercom) return;
    window.Intercom('update', { hide_default_launcher: true });
  }

  /**
   * Restore the default launcher button without opening the messenger.
   *
   * DEV-1955: The inverse of hideLauncher(). Brings back the floating bubble
   * so users can initiate chat on their own terms.
   */
  function showLauncher(): void {
    if (!enabled || !window.Intercom) return;
    window.Intercom('update', { hide_default_launcher: false });
  }

  return {
    enabled,
    bootChatBot,
    shutdownChatBot,
    updateChatBot,
    hideChatBot,
    showChatBot,
    hideLauncher,
    showLauncher,
  };
}
